import React, { Component } from 'react';
import {
  Router,
  Route,
  Redirect,
  Switch,
  matchPath,
} from 'react-router-dom';
import { Auth, Hub } from 'aws-amplify';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AuthState } from '@aws-amplify/ui-components';
import LogRocket from 'logrocket';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faSignal,
  faChevronCircleRight,
  faCheckCircle,
  faTimesCircle,
  faUser,
  faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import ReactGA from 'react-ga';
import { createBrowserHistory } from 'history'; // eslint-disable-line import/no-extraneous-dependencies
import mapEnabledWorkflows from './utils/map-enabled-workflows';
import SITE_PATHS from './config/sitePaths';
import GetUserInfoQuery from './appsync/queries/GetUser';
import appSyncClient from './appsync/client';
import LanguageProvider, { LanguageConsumer } from './config/LanguageContext';
import EmbeddableContextProvider, { contextExistsInStorage, clearSessionStorage } from './config/EmbeddableContext';
import { getAppURLFromLocalStorage } from './utils/appStateLocalStorage';
import { extractInviteFromURL, getQueryParameter } from './utils/urlHelpers';
import { workflow as workflowFromParams } from './utils/parameters';
import PrivateRoute from './hoc/PrivateRoute';
import RouteWithProps from './hoc/RouteWithProps';
import Loader from './components/Loader';
import Landing from './components/Landing';
import SignIn from './containers/SignIn';
import Invite from './containers/Invite';
import LoanList from './containers/LoanList';
import LoanDetails from './containers/LoanDetails';
import ChangePassword from './containers/ChangePassword';
import SignOut from './components/SignOut';
import SignInCallback from './components/SignInCallback';
import Header from './containers/Header';
import Footer from './components/Footer';
import SocialProvidersList from './containers/SocialProvidersList';
import ConectedSocialProfile from './components/SocialProviders/ConnectedSocialProfile';
import SocialProviderCallback from './components/SocialProviders/SocialProviderCallback';
import LegalTerms from './containers/LegalTerms';
import ThankYou from './containers/ThankYou';
import Modal from './components/Modal';
import Wordings from './config/stringTable';
import Banking from './containers/Banking';
import Utility from './containers/Utility';
import Payroll from './containers/Payroll';
import Chatbot from './containers/Chatbot';
import Wizard from './containers/Wizard';
import OAuth from './containers/OAuth';
import BankingThankYou from './containers/Banking/ThankYou';
import DealerIBVLandingPage from './containers/DealerIBVLandingPage';
import Prefill from './containers/Prefill';
import Leads from './containers/Leads';
import Offer from './containers/Offer';
import Standalone from './containers/Standalone';
import { getDataProviderList } from './services/truststartService';
import { MEASUREMENT_ID, LOGROCKET_APPID, LOGROCKET_CHILD_DOMAINS } from './config/configuration';

import {
  SUMMARY_CONTENT as termsSummary,
  CONTENT as termsContent,
} from './config/termsOfUseConstants';

import {
  SUMMARY_CONTENT as privacyPolicySummary,
  CONTENT as privacyPolicyContent,
} from './config/privacyPolicyConstants';

import './styles/App.scss';

library.add(faSignal, faChevronCircleRight, faCheckCircle, faTimesCircle, faUser, faArrowLeft);

if (LOGROCKET_APPID) {
  LogRocket.init(LOGROCKET_APPID, {
    mergeIframes: true,
    childDomains: LOGROCKET_CHILD_DOMAINS,
  });
  LogRocket.track('Initialize', {
    smartConsent: 'tsweb',
  });
  const { inviteId, action } = extractInviteFromURL();
  LogRocket.log('Invite from URL', { inviteId, action });
}

const existingPaths = Object.keys(SITE_PATHS).map((key) => SITE_PATHS[key]);

const is404Page = () => {
  const isValidRoute = existingPaths.some((route) => matchPath(window.location.pathname, { path: route, exact: true }));

  if (!isValidRoute) {
    ReactGA.set({ location: window.location.pathname + window.location.search });
    ReactGA.event({
      category: 'App',
      action: 'Page Not Found',
    });
  }

  return !isValidRoute;
};

const history = createBrowserHistory();

ReactGA.initialize(MEASUREMENT_ID);
const handleRouteChange = (location) => {
  if (!is404Page()) {
    ReactGA.set({ location: location.pathname + location.search });
    ReactGA.pageview(location.pathname + location.search);

    // Set the page title dynamically based on the route
    const routeTitleMap = {
      '/': 'Home',
      '/banking': 'Banking',
      '/invite': 'Invite',
      '/loan': 'Loan',
      '/terms': 'Terms of Use',
      '/privacy': 'Privacy Policy',
      '/thankyou': 'Thank You!',
      '/social-connector': 'Social Connections',
      '/utility': 'Utilities',
    };

    const defaultTitle = 'Smart Consent';
    const mainPath = `/${location.pathname.split('/')[1]}`;
    const pathTitle = routeTitleMap[mainPath];
    document.title = pathTitle ? `${defaultTitle} - ${pathTitle}` : defaultTitle;
  }
};

handleRouteChange(window.location);
history.listen(handleRouteChange);

const identifyUserToLogRocket = (user) => {
  if (!LOGROCKET_APPID) return;
  LogRocket.identify(user.id || user.sub, {
    name: user.name,
    email: user.email,
  });
};

class App extends Component {
  constructor(props) {
    super(props);
    const hasInvite = /\/invite\//.test(window.location.pathname);
    this.state = {
      fetchingAuthStatus: true,
      signedIn: false,
      userAccount: {},
      userProfile: {},
      showError: false,
      guestUserInfo: null,
      hasInvite,
      inviteId: hasInvite && window.location.pathname.replace(/\/invite\//g, ''),
    };
    this.handleEndOfWorkflow = this.handleEndOfWorkflow.bind(this);
  }

  componentDidMount() {
    if (!is404Page()) {
      ReactGA.set({ location: window.location.pathname + window.location.search });
      ReactGA.pageview(window.location.pathname + window.location.search);
    }

    Hub.listen('auth', ({ payload: { event, data } }) => {
      const eventInLowercase = event.toLowerCase();
      if (eventInLowercase === AuthState.SignIn && data) {
        this.fetchUserStatus();
      } else if (eventInLowercase === AuthState.SignOut) {
        this.setState(() => ({
          signedIn: false,
          fetchingAuthStatus: false,
          userAccount: {},
          userProfile: {},
          guestUserInfo: null,
          hasInvite: false,
          inviteId: null,
        }));
      }
    });

    this.fetchUserStatus();
  }

  handleEndOfWorkflow() {
    if (this.state.guestUserInfo) {
      this.setState({ guestUserInfo: null });
    }
    if (this.state.hasInvite) {
      this.setState({ hasInvite: false, inviteId: null });
    }
  }

  onAgreementAccepted = (guestUserData) => {
    if (guestUserData) {
      this.setState((state) => ({
        userProfile: {
          ...state.userProfile,
          agreement: {
            ...guestUserData,
            agreedToMobileTerms: true,
          },
        },
      }));
    } else {
      this.setState((state) => ({
        userProfile: {
          ...state.userProfile,
          agreement: {
            ...state.userProfile.agreement,
            agreedToMobileTerms: true,
          },
        },
      }));
    }
  };

  setStateAfterUserDataRetrieved({ userAccount = {}, userProfile }) {
    this.setState({
      signedIn: true,
      fetchingAuthStatus: false,
      userAccount,
      userProfile,
    });
  }

  setStateAfterUserDataRetrievedFailed() {
    this.setState({
      signedIn: true,
      fetchingAuthStatus: false,
      userProfile: {},
      showError: true,
    });
  }

  fetchUserStatus() {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        const userAccount = user.attributes || user;
        identifyUserToLogRocket(userAccount);
        appSyncClient.query({
          query: GetUserInfoQuery,
          fetchPolicy: 'network-only',
        })
          .then(({ data }) => {
            const userProfile = data.user || {};
            this.setStateAfterUserDataRetrieved({ userProfile, userAccount });
          })
          .catch((error) => {
            console.log('fetchUserData error:', error); // eslint-disable-line no-console
            this.setStateAfterUserDataRetrievedFailed();
          });
      })
      .catch((error) => {
        console.warn('Auth.currentAuthenticatedUser:', error); // eslint-disable-line no-console
        this.setState({
          signedIn: false,
          fetchingAuthStatus: false,
        });
      });
  }

  dismissModal() {
    this.setState({ showError: false });
  }

  renderSignInScreenRouting() {
    const { signedIn } = this.state;

    if (signedIn) {
      let action;
      if (window.location.pathname === SITE_PATHS.SIGNIN_CALLBACK) {
        const appURLDataString = getAppURLFromLocalStorage();
        if (appURLDataString !== '') {
          ({ action } = extractInviteFromURL(appURLDataString));
          localStorage.removeItem('appURLPath');
        }
      } else {
        ({ action } = extractInviteFromURL());
      }

      if (action === 'invite') {
        return (
          <RouteWithProps
            path={SITE_PATHS.INVITE_PAGE}
            component={Invite}
            history={this.props.history}
            onAgreementAccepted={this.onAgreementAccepted}
          />
        );
      }

      return (
        <Redirect exact from={SITE_PATHS.ROOT} to={SITE_PATHS.LOANS_LISTING} />
      );
    }

    return (
      <Route
        path={SITE_PATHS.SIGN_IN}
        render={
          () => (
            <SignIn
              clearGuestUserInfo={this.handleEndOfWorkflow}
              onAuthStateChanged={this.onAuthStateChanged}
            />
          )
        }
      />
    );
  }

  renderInvitePage() {
    if (this.state.signedIn) return null;

    if (!this.state.guestUserInfo && this.state.hasInvite) {
      Auth.currentCredentials()
        .then((user) => {
          this.setState((prevState) => ({
            guestUserInfo: {
              ...user,
              inviteId: prevState.inviteId,
            },
          }));
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log('ERROR in Auth.currentCredentials', error);
        });
      return <Loader />;
    }

    if (!this.state.guestUserInfo && ([SITE_PATHS.OAUTH, SITE_PATHS.STANDALONE].includes(window.location.pathname))) {
      Auth.currentCredentials()
        .then((user) => {
          this.setState({
            guestUserInfo: user,
          });
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log('ERROR in Auth.currentCredentials', error);
        });
      return <Loader />;
    }

    return (
      <RouteWithProps
        path={SITE_PATHS.INVITE_PAGE}
        component={Invite}
        history={this.props.history}
        onAgreementAccepted={this.onAgreementAccepted}
        isGuestUser
        user={this.state.guestUserInfo}
      />
    );
  }

  render() {
    const {
      fetchingAuthStatus,
      signedIn,
      userAccount,
      showError,
      userProfile,
    } = this.state;
    const contentOnly = getQueryParameter('contentonly');

    if (fetchingAuthStatus) {
      return <Loader />;
    }

    const isGuestUser = this.state.guestUserInfo;
    const workflow = mapEnabledWorkflows(workflowFromParams);
    const isStandalone = window.location.pathname.toLowerCase() === SITE_PATHS.STANDALONE;
    workflow.tswebEnabled = workflow.tswebEnabled || isStandalone;
    const isEmbedded = workflow && workflow.tswebEnabled;
    const clientRedirectURL = getQueryParameter('redirectUrl');
    const languageFromUrl = getQueryParameter('lang');
    const contextExistsInSession = contextExistsInStorage();
    const isReturningFromSocialAuth = window.location.pathname.match(/social-connector-callback/i);

    if (!isEmbedded && contextExistsInSession && !isReturningFromSocialAuth) {
      clearSessionStorage();
    }

    return (
      <EmbeddableContextProvider
        workflow={workflow}
        clientRedirectURL={clientRedirectURL}
        inviteInfo={extractInviteFromURL()}
      >
        <LanguageProvider language={languageFromUrl || userProfile.language}>
          <LanguageConsumer>
            {({ language, onUpdateLanguage }) => (
              <>
                <Modal
                  title={Wordings[language]['Error Occured']}
                  description={Wordings[language]['Network Error Try Again']}
                  buttonLabel={Wordings[language].ok}
                  doShow={showError}
                  toggle={() => this.dismissModal()}
                />
                <Router history={history}>
                  <>
                    <Header
                      isSignedIn={signedIn}
                      user={userAccount}
                      profile={userProfile}
                      hide={contextExistsInSession || isEmbedded || contentOnly}
                    />
                    <main className={[
                      'App',
                      !isEmbedded && 'headerSpacing',
                    ].filter((x) => !!x).join(' ')}
                    >
                      <Switch>
                        {this.renderSignInScreenRouting()}
                        {this.renderInvitePage()}
                        <Route
                          exact
                          path={SITE_PATHS.ROOT}
                          component={Landing}
                        />
                        <Route
                          exact
                          component={
                            // eslint-disable-next-line react/no-unstable-nested-components
                            (props) => (
                              <LegalTerms
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...props}
                                pageHeading="Privacy Policy"
                                summary={privacyPolicySummary}
                                content={privacyPolicyContent}
                                renderContentOnly={contentOnly}
                              />
                            )
                          }
                          path={SITE_PATHS.PRIVACY_POLICY}
                        />
                        <Route
                          exact
                          component={
                            // eslint-disable-next-line react/no-unstable-nested-components
                            (props) => (
                              <LegalTerms
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...props}
                                pageHeading="Terms of Use"
                                summary={termsSummary}
                                content={termsContent}
                                renderContentOnly={contentOnly}
                              />
                            )
                          }
                          path={SITE_PATHS.TERMS}
                        />
                        <RouteWithProps
                          component={DealerIBVLandingPage}
                          path={SITE_PATHS.DEALER_IBV_PAGE}
                        />
                        <RouteWithProps
                          exact
                          path={SITE_PATHS.PREFILL}
                          component={Prefill}
                        />
                        <RouteWithProps
                          exact
                          path={SITE_PATHS.LEADS_FROM_INVITE}
                          component={Leads}
                        />
                        <RouteWithProps
                          exact
                          path={SITE_PATHS.LEADS}
                          component={Leads}
                        />
                        <RouteWithProps
                          exact
                          path={SITE_PATHS.OFFER}
                          component={Offer}
                        />
                        <RouteWithProps
                          exact
                          component={BankingThankYou}
                          path={SITE_PATHS.BANKING_THANK_YOU}
                        />
                        <PrivateRoute
                          path={SITE_PATHS.SIGN_OUT}
                          component={SignOut}
                          isAllowedAccess={signedIn}
                        />
                        <PrivateRoute
                          path={SITE_PATHS.LOANS_LISTING}
                          component={LoanList}
                          isAllowedAccess={signedIn}
                          hasAgreed={userProfile.agreement || false}
                          isGuestUser={isGuestUser}
                        />
                        <PrivateRoute
                          path={SITE_PATHS.CHANGE_PASSWORD}
                          component={ChangePassword}
                          isAllowedAccess={signedIn}
                        />
                        <PrivateRoute
                          path={SITE_PATHS.SIGNIN_CALLBACK}
                          component={SignInCallback}
                          isAllowedAccess
                        />
                        <PrivateRoute
                          path={SITE_PATHS.STANDALONE}
                          component={Standalone}
                          isAllowedAccess
                        />
                        <Wizard workflow={workflow} onCompleted={this.handleEndOfWorkflow}>
                          {(onNextStep, resume) => (
                            <>
                              <PrivateRoute
                                path={SITE_PATHS.LOAN}
                                component={LoanDetails}
                                isAllowedAccess={signedIn || isGuestUser}
                                hasAgreed={userProfile.agreement || false}
                                guestUserInfo={this.state.guestUserInfo}
                                guestUserAgreement={this.state.userProfile}
                                hasInvite={this.state.hasInvite}
                                inviteId={this.state.inviteId}
                                onNextStep={onNextStep}
                                onUpdateLanguage={onUpdateLanguage}
                              />
                              <PrivateRoute
                                path={SITE_PATHS.OAUTH}
                                component={OAuth}
                                isAllowedAccess
                                resumeWizard={resume}
                              />
                              <PrivateRoute
                                path={SITE_PATHS.BANKING}
                                component={Banking}
                                isAllowedAccess={signedIn || isGuestUser}
                                isGuestUser={isGuestUser}
                                onNextStep={onNextStep}
                                getDataProviderList={getDataProviderList}
                              />
                              <PrivateRoute
                                exact
                                path={SITE_PATHS.UTILITY}
                                component={Utility}
                                isAllowedAccess={signedIn || isGuestUser}
                                isGuestUser={isGuestUser}
                                onNextStep={onNextStep}
                              />
                              <PrivateRoute
                                exact
                                path={SITE_PATHS.PAYROLL}
                                component={Payroll}
                                isAllowedAccess={signedIn || isGuestUser}
                                isGuestUser={isGuestUser}
                                onNextStep={onNextStep}
                              />
                              <PrivateRoute
                                path={SITE_PATHS.SOCIAL_CONNECTORS}
                                component={SocialProvidersList}
                                isAllowedAccess
                                isGuestUser={isGuestUser}
                                onNextStep={onNextStep}
                              />
                              <PrivateRoute
                                exact
                                path={SITE_PATHS.CHATBOT}
                                component={Chatbot}
                                isAllowedAccess={signedIn || isGuestUser}
                                isGuestUser={isGuestUser}
                                onNextStep={onNextStep}
                              />
                              <PrivateRoute
                                path={SITE_PATHS.THANK_YOU}
                                component={ThankYou}
                                isAllowedAccess
                              />
                              <PrivateRoute
                                path={SITE_PATHS.SOCIAL_CONNECTORS_CALLBACK}
                                component={SocialProviderCallback}
                                isAllowedAccess
                                resumeWizard={resume}
                              />
                              <PrivateRoute
                                path={SITE_PATHS.CONNECTED_SOCIAL_PROFILE}
                                component={ConectedSocialProfile}
                                isAllowedAccess
                                isGuestUser={isGuestUser}
                              />
                            </>
                          )}
                        </Wizard>
                        <Redirect to={SITE_PATHS.ROOT} />
                      </Switch>
                    </main>
                    <Footer hide={contentOnly || isEmbedded} language={language} />
                  </>
                </Router>
              </>
            )}
          </LanguageConsumer>
        </LanguageProvider>
      </EmbeddableContextProvider>
    );
  }
}

export default App;
